import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Plus } from "lucide-react";
import { Source } from '../lib/types';
import { sourceLabel } from '../lib/sources';
import { formatDateTime } from '../lib/text';
import { useAuth } from '../contexts/AuthContext';
import { PageLoader } from '../components/PageLoader';
import { PageError } from '../components/PageError';
import SourceLogo from '../components/SourceLogo';

// The workspace's connections as tiles; with none yet, the person goes straight to adding one.
export default function ConnectionsPage() {
    const { authFetch } = useAuth();
    const navigate = useNavigate();
    const [sources, setSources] = useState<Source[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        authFetch('/sources')
            .then(response => {
                if (!response.ok) throw new Error('Failed to load connections');
                return response.json();
            })
            .then((data: Source[]) => {
                if (data.length === 0) {
                    navigate('/connections/add?redirect=%2Fconnections', { replace: true });
                    return;
                }
                setSources(data);
                setLoading(false);
            })
            .catch(err => {
                setError(err instanceof Error ? err.message : 'Failed to load connections');
                setLoading(false);
            });
    }, [authFetch]);

    if (loading) return <PageLoader label="Loading connections..." />;
    if (error) return <PageError title="Failed to load connections" message={error} />;

    const tile = 'w-45 h-45 shrink-0 flex flex-col items-center justify-center gap-3 p-5 rounded-xl border text-center transition-all';

    return (
        <div>
            <div className="pb-6 mb-6 border-b border-softgrey">
                <h3 className="text-lg font-semibold text-graphite">Connections</h3>
                <p className="text-sm text-coolslate mt-1">The systems, document stores and messaging your company record is built from</p>
            </div>

            <div className="flex flex-wrap gap-3">
                {sources.map(source => {
                    const schedule = source.sync_schedule !== 'off' && source.next_sync_at
                        ? `Syncs ${source.sync_schedule}; next ${formatDateTime(source.next_sync_at, false)}`
                        : `Updated ${formatDateTime(source.updated_at)}`;
                    return (
                        <Link
                            key={source.id}
                            to={`/connections/${source.id}`}
                            title={schedule}
                            className={`${tile} bg-white border-softgrey hover:border-brand hover:shadow-md`}
                        >
                            <div className="bg-white w-20 h-20 rounded-xl border border-softgrey p-3 flex items-center justify-center">
                                <SourceLogo type={source.source_type} size="2xl" alt="" />
                            </div>
                            <div className="min-w-0 w-full">
                                <div className="text-sm font-medium text-graphite truncate">{source.source}</div>
                                <div className="text-xs text-coolslate truncate">{sourceLabel(source.source_type)}</div>
                            </div>
                        </Link>
                    );
                })}
                <Link
                    to="/connections/add?redirect=%2Fconnections"
                    className={`${tile} border-dashed border-softgrey text-coolslate hover:border-brand hover:text-brand-dark`}
                >
                    <div className="w-20 h-20 rounded-xl flex items-center justify-center">
                        <Plus className="w-10 h-10" />
                    </div>
                    <span className="text-sm font-medium">Add a connection</span>
                </Link>
            </div>
        </div>
    );
};
